import React, { useEffect, useState, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { Button } from "@material-ui/core";
import api from "../../services/api";
import { parseISO, format } from "date-fns";
import AppointmentActionsModal from "../AppointmentActionsModal";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import AssignContactModal from "../AssignContactModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import moment from "moment";
import "moment/locale/es";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  calendarContainer: {
    flexGrow: 1,
    minHeight: 0, 
    marginTop: theme.spacing(1),
    "& .fc": {
      height: "100%", 
    }
  },
  rescheduleMessage: {
    padding: 10,
    marginBottom: 10,
    background: theme.palette.background.default,
    color: "#ff0000",
    borderRadius: 8,
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between'
  },
  rescheduleWarning: {
    color: "#333", 
    backgroundColor: "#fff9c4", 
    borderColor: "#fdd835",
    padding: 10,
    marginBottom: 10,
    borderRadius: 8,
  },
  cancelButton: {
    marginLeft: 20
  }
}));

const DoctorCalendar = ({
  doctorId,
  ticket,
  isAssignMode = false,
  onClose,
  companyId: companyIdProp,
  allowAssignWithoutTicket = true,
  selectedTitleId,
  patientNameToAssign,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const companyId = companyIdProp || ticket?.whatsapp?.companyId || user?.companyId;

  const [appointments, setAppointments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [openAppointmentActions, setOpenAppointmentActions] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [slotToAssign, setSlotToAssign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingSlot, setPendingSlot] = useState(null);

  const calendarRef = React.useRef(null);
  const [currentView, setCurrentView] = useState("timeGridDay");
  
  const slotDuration = schedules.length > 0
    ? (() => {
        const minutes = schedules[0].appointmentDuration;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return String(hours).padStart(2, "0") + ":" + String(mins).padStart(2, "0") + ":00";
      })()
    : "00:30:00";

  function getCalendarTimeRange(schedules) {
    if (!schedules || schedules.length === 0) {
      return { slotMinTime: "00:00:00", slotMaxTime: "24:00:00" };
    }
    const startTimes = schedules.map((s) => s.startTime);
    const endTimes = schedules.map((s) => s.endTime);
    const minStart = startTimes.reduce((min, t) => (t < min ? t : min), startTimes[0]);
    const maxEnd = endTimes.reduce((max, t) => (t > max ? t : max), endTimes[0]);
    return { slotMinTime: minStart, slotMaxTime: maxEnd };
  }

  const { slotMinTime, slotMaxTime } = getCalendarTimeRange(schedules);

  const fetchData = async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const [userRes, appointmentsRes] = await Promise.all([
        api.get("/users/" + doctorId),
        api.get("/appointments", { params: { doctorId } }),
      ]);

      setSchedules(userRes.data.doctorSchedules || []);
      setAppointments(appointmentsRes.data || []);
    } catch (err) {
      toast.error("Hubo un error inesperado");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [doctorId]);

  const generateAvailableSlots = () => {
    const slots = [];
    const today = new Date();
    for (let i = 0; i < 50; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayOfWeek = date.getDay();

      schedules
        .filter((s) => s.dayOfWeek === dayOfWeek)
        .forEach((s) => {
          const [sh, sm] = s.startTime.split(":").map(Number);
          const [eh, em] = s.endTime.split(":").map(Number);
          let slotStart = new Date(date);
          slotStart.setHours(sh, sm, 0, 0);
          const slotEndLimit = new Date(date);
          slotEndLimit.setHours(eh, em, 0, 0);

          while (slotStart < slotEndLimit) {
            const slotEnd = new Date(slotStart);
            slotEnd.setMinutes(slotEnd.getMinutes() + s.appointmentDuration);

            if (slotEnd > new Date()) {
              const overlaps = appointments.some(function(app) {
                if (app.status !== "scheduled") return false;
                const appStart = new Date(app.date + "T" + app.startTime);
                const appEnd = new Date(app.date + "T" + app.endTime);
                return slotStart < appEnd && slotEnd > appStart;
              });

              if (!overlaps && slotEnd <= slotEndLimit) {
                slots.push({
                  id: slotStart.toISOString() + "_" + slotEnd.toISOString(),
                  start: slotStart.toISOString(),
                  end: slotEnd.toISOString(),
                });
              }
            }

            slotStart = slotEnd;
          }
        });
    }
    return slots;
  };

  const availableSlots = useMemo(generateAvailableSlots, [schedules, appointments]);

  const statusColors = {
    completed: { background: "#4caf50", text: "#ffffff" },
    scheduled: { background: "#2196f3", text: "#ffffff" },
    cancelled: { background: "#f44336", text: "#ffffff" },
    default: { background: "#9e9e9e", text: "#000000" },
  };

  const sortedAppointments = [...appointments].sort((a, b) => {
    const statusOrder = { scheduled: 0, cancelled: 1 };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    return new Date(a.date + "T" + a.startTime) - new Date(b.date + "T" + b.startTime);
  });

  const events = [
    ...sortedAppointments.map((a) => {
      const colors = statusColors[a.status] || statusColors.default;
      const start = new Date(a.date + "T" + a.startTime);
      const end = new Date(a.date + "T" + a.endTime);
      const isEditable = a.status === "scheduled" && currentView !== "dayGridMonth";
      let eventTitle;

      if (a.patientName) {
        const schedulerName = a.contact?.name || "Desconocido";
        eventTitle = a.patientName + " (agendado por " + schedulerName + ")";
      } else {
        eventTitle = a.contact?.name || "Sin nombre";
      }

      if (a.notes) {
        eventTitle = eventTitle + " - " + a.notes;
      }
      
      return {
        id: "appt_" + a.id,
        title: eventTitle, 
        start: start.toISOString(),
        end: end.toISOString(),
        color: colors.background,
        textColor: colors.text,
        editable: isEditable,
        startEditable: isEditable,
        durationEditable: false,
        extendedProps: { status: a.status }
      };
    }),
    ...((isAssignMode || allowAssignWithoutTicket)
      ? availableSlots.map((s) => ({
          id: s.id,
          start: s.start,
          end: s.end,
          backgroundColor: s.id === selectedSlotId ? "#b3d4ff" : "#bdbdbd",
          borderColor: "transparent",
          editable: false,
          classNames: ["available-slot-custom"],
          extendedProps: { isSlot: true, slotId: s.id },
        }))
      : []),
  ];

  const handleAssignAppointment = async (slot, contactOrId, patientName) => {
    const finalContact = ticket ? ticket.contact : (contactOrId instanceof Object ? contactOrId : null);
    const contactId = ticket ? ticket.contactId : (finalContact ? finalContact.id : null);

    if (!finalContact || !contactId) {
      toast.error("No se pudo determinar el contacto para la cita.");
      return;
    }
    
    if (!selectedTitleId) {
      toast.error("Se debe seleccionar la especialidad del doctor.");
      return;
    }
    
    try {
      const start = typeof slot.start === "string" ? parseISO(slot.start) : slot.start;
      const end = typeof slot.end === "string" ? parseISO(slot.end) : slot.end;

      let finalPatientName = patientName;
      if (finalContact.name === patientName) {
          finalPatientName = null;
      }
      
      const { data } = await api.post("/appointments", {
        doctorId,
        contactId: contactId,
        companyId: companyId,
        ticketId: ticket?.id || null,
        professionalTitleId: selectedTitleId,
        date: format(start, "yyyy-MM-dd"),
        startTime: format(start, "HH:mm"),
        endTime: format(end, "HH:mm"),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notes: "",
        patientName: finalPatientName,
      });

      await fetchData();
      
      if (data.messageError) {
        toast.warn(data.messageError, { autoClose: 10000 });
      } else {
        toast.success("Cita asignada correctamente");
      }
      
      if (onClose) onClose();

    } catch (err) {
      toastError(err);
      console.error("Error creando cita", err);
    }
  };

  const handleRescheduleAppointment = async (appointmentId, slot) => {
    try {
      const start = slot.start instanceof Date ? slot.start : parseISO(slot.start);
      const end = slot.end instanceof Date ? slot.end : parseISO(slot.end);

      const { data } = await api.patch("/appointments/" + appointmentId, {
        date: format(start, "yyyy-MM-dd"),
        startTime: format(start, "HH:mm"),
        endTime: format(end, "HH:mm"),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      setIsRescheduleMode(false);
      setAppointmentToReschedule(null);
      await fetchData();
      
      if (data.messageError) {
        toast.warn(data.messageError, { autoClose: 10000 });
      } else {
        toast.success("Cita reprogramada correctamente");
      }
      if (onClose) onClose();
    } catch (err) {
      toastError(err);
      console.error("Error reprogramando cita", err);
    }
  };

  const onEventClick = (info) => {
    const eventId = info.event.id;
    if (info.event.extendedProps.isSlot) {
      const start = info.event.start instanceof Date ? info.event.start : parseISO(info.event.start);
      const end = info.event.end instanceof Date ? info.event.end : parseISO(info.event.end);

      if (isRescheduleMode && appointmentToReschedule) {
        const appt = appointments.find(a => String(a.id) === String(appointmentToReschedule.id)) || appointmentToReschedule;

        const start = info.event.start instanceof Date ? info.event.start : parseISO(info.event.start);
        const end = info.event.end instanceof Date ? info.event.end : parseISO(info.event.end);

        const originalStart = appt && appt.date && appt.startTime
          ? new Date(appt.date + "T" + appt.startTime)
          : (appt.start ? new Date(appt.start) : start);

        const originalEnd = appt && appt.date && appt.endTime
          ? new Date(appt.date + "T" + appt.endTime)
          : (appt.end ? new Date(appt.end) : end);

        setPendingSlot({
          appointmentId: appt.id,
          type: "reschedule",
          start,
          end,
          originalStart,
          originalEnd,
          contactName: appt?.patientName || appt?.contact?.name || ticket?.contact?.name || "Sin nombre",
        });

        setConfirmModalOpen(true);
        return;
      }
      
      if (!selectedTitleId) {
        toast.info("Seleccione la especialidad antes de asignar la cita");
        return;
      }
    
      if (ticket) {
        setSelectedSlotId(eventId);
        setPendingSlot({
          type: "assign",
          start,
          end,
          patientName: patientNameToAssign,
          contactId: ticket.contactId,
        });
        setConfirmModalOpen(true);
        return;
      }

      if (allowAssignWithoutTicket) {
        setSlotToAssign({ id: eventId, start, end });
        setSelectedContact(null);
        setAssignModalOpen(true);
      }

      return;
    }

    const appointment = appointments.find((a) => "appt_" + a.id === eventId);
    if (!appointment) return;
    if (appointment.status === "scheduled" || appointment.status === "completed") {
      setSelectedAppointment(appointment);
      setOpenAppointmentActions(true);
    }
  };

  const toMinuteTs = (d) => {
    const dt = new Date(d);
    dt.setSeconds(0, 0);
    dt.setMilliseconds(0);
    return dt.getTime();
  };
  
  const handleDatesSet = (dateInfo) => {
      setCurrentView(dateInfo.view.type);
  };
  
  const eventAllow = (dropInfo, draggedEvent) => {
    if (!draggedEvent || draggedEvent.extendedProps?.isSlot || draggedEvent.extendedProps?.status !== "scheduled") {
      return false;
    }

    const dropStartTs = toMinuteTs(dropInfo.start);
    const dropEndTs = toMinuteTs(dropInfo.end);

    const candidateSlots = availableSlots.filter(slot => {
      const slotStartTs = toMinuteTs(slot.start);
      const slotEndTs = toMinuteTs(slot.end);
      return dropEndTs > slotStartTs && dropStartTs < slotEndTs;
    });

    if (candidateSlots.length === 0) return false;

    const closestSlot = candidateSlots.reduce((closest, slot) => {
      const slotStartTs = toMinuteTs(slot.start);
      return Math.abs(dropStartTs - slotStartTs) < Math.abs(dropStartTs - toMinuteTs(closest.start)) ? slot : closest;
    }, candidateSlots[0]);

    dropInfo.start = new Date(closestSlot.start);
    dropInfo.end = new Date(closestSlot.end);

    return true;
  };

  const handleEventDrop = (info) => {
    const event = info.event;
    const appointmentId = event.id.replace("appt_", "");

    if (event.extendedProps.status !== "scheduled") {
      if (typeof info.revert === "function") info.revert();
      return;
    }

    const appointment = appointments.find(a => String(a.id) === String(appointmentId));

    const dropStartTs = toMinuteTs(event.start);
    const validSlot = availableSlots.reduce((closest, slot) => {
      const slotStartTs = toMinuteTs(slot.start);
      return Math.abs(dropStartTs - slotStartTs) < Math.abs(dropStartTs - toMinuteTs(closest.start)) ? slot : closest;
    }, availableSlots[0]);

    if (!validSlot) {
      if (typeof info.revert === "function") info.revert();
      toast.info("Solo puede mover la cita a un espacio disponible del horario del doctor");
      return;
    }

    if (typeof info.revert === "function") info.revert();

    const originalStart = appointment && appointment.date && appointment.startTime
      ? new Date(appointment.date + "T" + appointment.startTime)
      : event.start;
    const originalEnd = appointment && appointment.date && appointment.endTime
      ? new Date(appointment.date + "T" + appointment.endTime)
      : event.end;

    setPendingSlot({
      appointmentId,
      start: validSlot.start,
      end: validSlot.end,
      type: "reschedule",
      originalStart,
      originalEnd,
      contactName: appointment?.patientName || appointment?.contact?.name || ticket?.contact?.name || "Sin nombre",
    });
    setConfirmModalOpen(true);
  };
  
  const handleEventDidMount = (info) => {
    const bgColor = info.event.backgroundColor || info.event.color;
    const textColor = info.event.textColor;

    info.el.style.cursor = "pointer";
      
    info.el.style.backgroundColor = bgColor;
    info.el.style.borderColor = "transparent";
    const textElement = info.el.querySelector(".fc-event-title") || 
                        info.el.querySelector(".fc-event-main-frame") || 
                        info.el.querySelector(".fc-event-main");
      
    if (textElement) {
      textElement.style.color = textColor || "#FFFFFF";
    } else {
      info.el.style.color = textColor || "#FFFFFF";
    }
  };

  const renderEventContent = (info) => {
    const baseStyle = {
      height: "100%",
      display: "flex",
      alignItems: "center",
      padding: "0 4px",
      fontSize: "0.8rem",
      fontWeight: "600",
      color: "#333",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    };

    if (info.event.extendedProps.isSlot) {
      const start = moment(info.event.start).format("HH:mm");
      const end = moment(info.event.end).format("HH:mm");
      return (
        <div style={baseStyle}>{start} - {end}</div>
      );
    }

    const start = moment(info.event.start).format("HH:mm");

    return (
      <div style={{ ...baseStyle, color: info.event.textColor || "#FFFFFF" }}>
        {start} - {info.event.title}
      </div>
    );
  };

  const eventOverlap = (stillEvent, movingEvent) => {
    if (!stillEvent) return true;
    if (stillEvent.extendedProps?.isSlot) return true;
    return false;
  };
      
  const fetchContacts = async (search = "") => {
    if (!companyId) return setContacts([]);
    setContactsLoading(true);
    try {
      const res = await api.get("/contacts", {
        params: { companyId, searchParam: search, pageNumber: 1 },
      });
      const list = res.data?.contacts ?? res.data ?? [];
      setContacts(list);
    } catch (err) {
      console.error("Error cargando contactos", err);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    if (assignModalOpen) fetchContacts(contactSearch);
  }, [assignModalOpen, contactSearch]);

  const confirmAssignSelectedContact = async (patientName) => {
    if (!slotToAssign || !selectedContact) return;
    try {
      await handleAssignAppointment(slotToAssign, selectedContact, patientName); 

      setAssignModalOpen(false);
      setSlotToAssign(null);
      setSelectedContact(null);
    } catch (err) {
      toast.error("Hubo un error asignando");
      console.error("Error confirmando asignación", err);
    }
  };
  
  const handleCancelReschedule = () => {
    setIsRescheduleMode(false); 

    setAppointmentToReschedule(null); 
  };

  if (loading)
    return <div style={{ textAlign: "center", padding: 20 }}>Cargando...</div>;
  
  const eventOrderString = 
    currentView === "dayGridMonth"
      ? "start,status,id"
      : "status,start,id";

  return (
    <div className={classes.root}>
      {isRescheduleMode && appointmentToReschedule && (
        <div className={classes.rescheduleMessage}>
          <span>
            Haga click en el nuevo horario para la cita de{" "}
            <b>{appointmentToReschedule.patientName || appointmentToReschedule.contact?.name}</b>
          </span>

          <Button
            variant="outlined"
            color="secondary" 
            size="small"
            onClick={handleCancelReschedule}
            className={classes.cancelButton}
          >
            Cancelar Reprogramación
          </Button>
        </div>
      )}
      {currentView === "dayGridMonth" && !isRescheduleMode && (
        <div className={classes.rescheduleWarning}>
          ⚠️ En la vista Mensual, no se permite arrastrar citas. Para reprogramar, haga Click en la cita, y seleccione la opción Reprogramar.
        </div>
      )}
      <div className={classes.calendarContainer}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          locale={esLocale}
          initialView="timeGridDay"
          editable={true}
          eventStartEditable={true}
          eventDurationEditable={false}
          eventOverlap={eventOverlap}
          eventAllow={eventAllow}
          events={events}
          eventDrop={handleEventDrop}
          eventOrder={eventOrderString}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          slotDuration={slotDuration}
          datesSet={handleDatesSet}
          timeZone="local"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          eventClick={onEventClick}
          eventContent={renderEventContent}
          eventDidMount={handleEventDidMount}
          height="100%" 
        />
      </div>

      <AppointmentActionsModal
        open={openAppointmentActions}
        appointment={selectedAppointment}
        mode={selectedAppointment?.status === "scheduled" ? "edit" : "view"}
        onClose={(shouldRefresh) => {
          setOpenAppointmentActions(false);
          if (shouldRefresh) {
            fetchData();
          }
        }}
        onCancelAppointment={async (id) => {
          try {
            const { data } = await api.patch("/appointments/" + id, { status: "cancelled" });
            if (data.messageError) {
              toast.warn(data.messageError, { autoClose: 10000 });
            } else {
              toast.success("Cita cancelada correctamente");
            }
            setOpenAppointmentActions(false);
            await fetchData();
          } catch(err) {
            toastError(err);
          }
        }}
        onCompleteAppointment={async (id) => {
          try {
            await api.patch("/appointments/" + id, { status: "completed" });
            toast.success("Cita finalizada correctamente.");
            setOpenAppointmentActions(false);
            fetchData();
          } catch (err) {
            toastError(err);
          }
        }}
        onReprogram={(appointment) => {
          setAppointmentToReschedule(appointment);
          setIsRescheduleMode(true);
          setOpenAppointmentActions(false);
        }}
      />

      {/* Modal para seleccionar contacto cuando no hay ticket */}
      <AssignContactModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        companyId={ticket?.companyId || ticket?.whatsapp.companyId}
        contacts={contacts}
        contactsLoading={contactsLoading}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        contactSearch={contactSearch}
        setContactSearch={setContactSearch}
        confirmAssignSelectedContact={confirmAssignSelectedContact}
        slotToAssign={slotToAssign}
      />
      
      <ConfirmationModal
        open={confirmModalOpen}
        maxWidth="xs"
        fullWidth={true}
        title={pendingSlot?.type === "reschedule" ? "Reprogramar cita" : "Confirmar cita"}
        onClose={() => {
          setConfirmModalOpen(false);
          setPendingSlot(null);
        }}
        onConfirm={async () => {
          if (!pendingSlot) return;

          if (pendingSlot.type === "assign") {
            await handleAssignAppointment(pendingSlot, pendingSlot.contactId, pendingSlot.patientName);
          } else if (pendingSlot.type === "reschedule") {
            await handleRescheduleAppointment(
              pendingSlot.appointmentId,
              pendingSlot
            );
          }

          setConfirmModalOpen(false);
          setPendingSlot(null);
        }}
      >
        {pendingSlot && (
          <div style={{ lineHeight: 1.6 }}>
            <p>
              {pendingSlot.type === "reschedule"
                ? "La cita de"
                : "Se asignará la cita a"}{" "}
              <b>{pendingSlot.patientName || "Sin nombre"}</b>
              {pendingSlot.type === "reschedule" &&
              pendingSlot.originalStart &&
              pendingSlot.originalEnd ? (
                moment(pendingSlot.originalStart).isSame(pendingSlot.start, "day") ? (
                  <>
                    {" "}
                    se moverá de{" "}
                    <b>
                      {moment(pendingSlot.originalStart).format("HH:mm")}-{moment(
                        pendingSlot.originalEnd
                      ).format("HH:mm")}
                    </b>{" "}
                    a{" "}
                    <b>
                      {moment(pendingSlot.start).format("HH:mm")}-{moment(
                        pendingSlot.end
                      ).format("HH:mm")}
                    </b>
                  </>
                ) : (
                  <>
                    {" "}
                    se moverá de{" "}
                    <b>
                      {moment(pendingSlot.originalStart)
                        .locale("es")
                        .format("dddd, D [de] MMMM [de] YYYY")}{" "}
                      {moment(pendingSlot.originalStart).format("HH:mm")}-{moment(
                        pendingSlot.originalEnd
                      ).format("HH:mm")}
                    </b>{" "}
                    a{" "}
                    <b>
                      {moment(pendingSlot.start)
                        .locale("es")
                        .format("dddd, D [de] MMMM [de] YYYY")}{" "}
                      {moment(pendingSlot.start).format("HH:mm")}-{moment(
                        pendingSlot.end
                      ).format("HH:mm")}
                    </b>
                  </>
                )
              ) : (
                <>
                  {" "}
                  para el día{" "}
                  <b>
                    {moment(pendingSlot.start)
                      .locale("es")
                      .format("dddd, D [de] MMMM [de] YYYY")}
                  </b>{" "}
                  desde <b>{moment(pendingSlot.start).format("HH:mm")}</b> hasta{" "}
                  <b>{moment(pendingSlot.end).format("HH:mm")}</b>
                </>
              )}
            </p>
          </div>
        )}
      </ConfirmationModal>

    </div>
  );
};

export default DoctorCalendar;